import { useMediaQuery } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect } from 'react'
import styled from 'styled-components'
import { LineType } from 'interfaces'
import { Config } from '../../../../../src/config/ConfigManager.ts'
import { BannerPromoDrawer } from '../../../../../src/features/search/Results/BannerPromoDrawer.tsx'
import { useUiStateStore } from '../../../../../src/store/uiState/UiStateStore.ts'
import { breakpoints } from '../../../../../src/theme/breakpoints.ts'
import { BannerAdsSearchResults } from '../../../../../src/ui-components/util/BannerAdsSearchResults.tsx'
import { useResultsStore } from '../../store/ResultsStore.ts'
import { StockOptions } from '../../constants.ts'
import BackToTopButton from './BackToTopButton.tsx'
import FitmentDisclaimer from './FitmentDisclaimer.tsx'
import NoResultsWrapper from './NoResultsWrapper.tsx'
import ResultsContentHeader from './ResultsContentHeader.tsx'
import SaytRecommendationsBox from './SaytRecommendationsBox.tsx'
import { FilterChipBar } from '../../../../../src/features/filterResults/components/FilterChipBar.tsx'

import { StoreInstances } from 'src/store/StoreInstancesContainer.ts'
import { SearchStatus } from '../../../../../src/features/search/Results/components/multiSeller/storeCard/Enums.ts'
import TableWrapper from './table/Wrapper.tsx'
import { UnableToConnect } from './UnableToConnect.tsx'
import InquiryTimeoutError from './InquiryTimeoutError'
import { useSearchStore } from '../../../../../src/store/search/SearchStore'
interface Props {
  onClickFilters: () => void
}

export const ResultsContent = observer((props: Props): ReactElement => {
  const resultsStore = useResultsStore()
  const searchStore = useSearchStore()

  const { searchResults, filters, searchDiagramsByPartsBinId, sellerDetails } =
    resultsStore
  const { onClickFilters } = props
  const uiStore = useUiStateStore()

  const matches = useMediaQuery('(max-width:1460px)')

  const parts =
    searchResults?.parts?.filter((part) => part.type === LineType.part) ?? []

  useEffect(() => {
    if (resultsStore?.partsBinId === undefined) return
    searchDiagramsByPartsBinId(resultsStore?.partsBinId)
  }, [resultsStore?.partsBinId, searchDiagramsByPartsBinId])

  const { totalCount, filteredCount, isSnwFinished } = searchResults
  const totalResults = filteredCount ?? totalCount ?? 0

  useEffect(() => {
    let fetchPartsTimeout: ReturnType<typeof setTimeout>
    let fetchPartsInterval: ReturnType<typeof setTimeout>
    const selectedStockOption = resultsStore.getDefaultStockFilterOption(
      StoreInstances?.userStore?.preferences?.display_filtering,
      StoreInstances?.userStore?.preferences?.display_userFiltering
    )
    if (
      filteredCount &&
      totalCount &&
      filteredCount < totalCount &&
      !isSnwFinished &&
      selectedStockOption !== StockOptions.SHOW_ALL &&
      resultsStore.filters.length === 0
    ) {
      fetchPartsInterval = setInterval(() => {
        if (!isSnwFinished) {
          resultsStore.searchByPartBin()
        } else {
          clearInterval(fetchPartsInterval)
        }
      }, 3000)

      // Stop the interval after reaching a max threshold (i.e 5 times)
      fetchPartsTimeout = setTimeout(() => {
        clearInterval(fetchPartsInterval)
      }, 15000)
    }
    // clear the timer when component unmouts
    return () => {
      clearInterval(fetchPartsInterval)
      clearTimeout(fetchPartsTimeout)
    }
  }, [filteredCount, isSnwFinished, resultsStore, totalCount])

  return (
    <Wrapper filtersCollapsed={matches}>
      <BannerAd>
        {import.meta.env.VITE_FEATURE_BANNER_ADS_SEARCH_RESULTS === 'true' && (
          <BannerAdsSearchResults />
        )}
      </BannerAd>
      {!searchStore.isLoading &&
        searchStore.saytRecommendations &&
        searchStore.saytRecommendations.length > 0 && (
          <SaytRecommendationsBox />
        )}
      <ResultsContentHeader
        totalResults={totalResults}
        onClickFilters={onClickFilters}
      />
      {!matches && <FilterChipBar />}
      {searchStore.showPartsNotFitVehicleLabel && totalResults > 0 && (
        <FitmentDisclaimer />
      )}
      {searchStore.inquiryTimeoutError && <InquiryTimeoutError />}

      {!resultsStore.isLoading &&
      parts.length === 0 &&
      !searchStore.inquiryTimeoutError &&
      sellerDetails?.searchStatus !== SearchStatus.ERROR ? (
        <NoResultsWrapper filters={filters} />
      ) : (
        <>
          {/* Removed isLoading check below because we still want to display results when the next page is loading */}
          {parts.length > 0 && (
            <>
              <BackToTopButton />
              <TableWrapper parts={parts} />
            </>
          )}
          {sellerDetails?.searchStatus === SearchStatus.ERROR && (
            <UnableToConnect showContactUs={!sellerDetails?.isMyPlaceStore} />
          )}
        </>
      )}
      {Config.promotionsEnabled && uiStore.isPromoEnabled() && (
        <BannerPromoDrawer />
      )}
    </Wrapper>
  )
})

ResultsContent.displayName = 'ResultsContent'

interface WrapperProps {
  filtersCollapsed: boolean
}

const BannerAd = styled.div`
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: none;
  }
`
const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  overflow-x: clip;
  margin-left: ${(p) => (p.filtersCollapsed ? '20px' : '10px')};
  margin-right: 20px;
  margin-bottom: 15px;

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    margin-left: 16px;
    margin-right: 16px;
  }
`
